import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { formInput, formRadioGroup, formRow, formSelect, RktFormComponent } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { FinancialAccountDetails, FormModel, TransactionAdjustmentRequest } from '@shared/models';
import { FinancialAccountService, NotificationService, TransactionService } from '@shared/services';
import { ErrorUtils, Patterns } from '@shared/utils';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-create-transaction-adjustment',
  imports: [RktButtonDirective, RktFormComponent, FormsModule, ReactiveFormsModule, RktIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './create-transaction-adjustment.component.html',
})
export class CreateTransactionAdjustmentComponent {
  activeModal = inject(ActiveModal);

  store = inject(Store);

  financialAccountService = inject(FinancialAccountService);

  transactionService = inject(TransactionService);

  notificationService = inject(NotificationService);

  transactionAdjustmentForm = new FormGroup({});

  isLoading = false;

  formSubmitError!: string;

  formFields = [
    formRadioGroup({
      key: 'adjustmentType',
      label: 'Adjustment Type',
      props: {
        placeholder: 'Select',
        options: [
          { label: 'Credit', value: 'CREDIT' },
          { label: 'Debit', value: 'DEBIT' },
        ],
        required: true,
      },
    }),
    formRow([
      formInput({
        key: 'amount',
        label: 'Amount',
        props: { required: true, pattern: Patterns.NUMBER_ONLY },
        validation: {
          messages: {
            pattern: 'Invalid amount',
          },
        },
      }),
      formInput({
        key: 'currency',
        label: 'Currency',
        props: { disabled: true },
      }),
    ]),
    formRow([
      formSelect({
        key: 'adjustmentReason',
        label: 'Adjustment Reason',
        props: {
          placeholder: 'Select',
          options: [
            { label: 'Deposit Transaction', value: 'DEPOSIT_TRANSACTION' },
            { label: 'Reconciliation', value: 'RECONCILIATION' },
            { label: 'Withdrawl Transaction', value: 'WITHDRAWAL_TRANSACTION' },
            { label: 'Error Correction', value: 'ERROR_CORRECTION' },
          ],
          required: true,
        },
      }),
    ]),
    formRow([
      formSelect({
        key: 'adjustmentAccount',
        label: 'Adjustment Financial Account',
        props: {
          placeholder: 'Select',
          options: [],
          required: true,
        },
      }),
    ]),
  ];

  formModel: FormModel<{
    currency: string;
    amount: string;
    adjustmentType: string;
    adjustmentReason: string;
    adjustmentAccount: string;
  }> = {};

  financialAccount?: FinancialAccountDetails;

  private destroy$ = new Subject<void>();

  onModalInit(financialAccount: FinancialAccountDetails): void {
    this.financialAccount = financialAccount;
    if (financialAccount) {
      this.formModel = {
        currency: financialAccount.currency,
      };
    }
    this.getAdjustmentFinancialAccounts();
  }

  private getAdjustmentFinancialAccounts(): void {
    this.financialAccountService
      .getFinancialAccounts({ activeFilters: { internalAccountRoles: 'ADJUSTMENT', currency: this.formModel.currency } })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (financialAccounts) => {
          if (this.formFields[3]?.fieldGroup && this.formFields[3]?.fieldGroup[0]?.props) {
            this.formFields[3].fieldGroup[0].props.options = financialAccounts.items.map((account) => ({
              label: `${account.name ?? account.subtype} | ***${account.bankAccount?.accountNumberTail}`,
              value: account.id,
            }));
          }
        },
      });
  }

  onCreate() {
    if (!this.financialAccount) return;

    this.isLoading = true;
    const payload = {
      amount: this.formModel.amount,
      businessAccountId: this.financialAccount?.businessAccountId,
      currency: this.formModel.currency,
      adjustmentReason: this.formModel.adjustmentReason,
    } as TransactionAdjustmentRequest;

    if (this.formModel.adjustmentType === 'CREDIT') {
      payload.debitFinancialAccountId = this.formModel.adjustmentAccount!;
      payload.creditFinancialAccountId = this.financialAccount?.id;
    } else {
      payload.debitFinancialAccountId = this.financialAccount?.id;
      payload.creditFinancialAccountId = this.formModel.adjustmentAccount!;
    }

    this.transactionService.createAdjustmentTransaction(payload).subscribe({
      next: (transaction) => {
        if (transaction) {
          this.activeModal.close(true);
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.formSubmitError = ErrorUtils.parseExtendedError(error);
        this.notificationService.displayError(this.formSubmitError, 'Error');
      },
    });
  }
}
